import React, { useState } from "react";
import styles from "./StepMonetizationControls.module.css";
import heroImg from "../assets/content-monetization.png";
import stayInControlImg from "../assets/stay-in-control.png";
import { showToast } from "../../../../utils/toast";
import {
  CrownIcon,
  PaidMessagingIcon,
  TipsIcon,
  PaidPhotosIcon,
  PaidVideoIcon,
  PrivateShowsIcon,
  SubscriptionsCrownIcon,
  ContentBundlesIcon,
  SocialLinkIcon,
  CustomRequestsIcon,
  PrivateShowsExtendedIcon,
} from "../icons/MonetizationControlsIcons";

interface MonetizationOption {
  id: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  enabled: boolean;
  isCoreChannel?: boolean;
}

const INITIAL_OPTIONS: MonetizationOption[] = [
  // Left Column
  {
    id: "messaging",
    title: "Paid Messaging",
    desc: "Charge for direct messages and interactions with your audience.",
    icon: <PaidMessagingIcon />,
    enabled: true,
    isCoreChannel: true,
  },
  {
    id: "tips",
    title: "Tips",
    desc: "Accept tips from your audience as a form of support.",
    icon: <TipsIcon />,
    enabled: true,
    isCoreChannel: true,
  },
  {
    id: "photos",
    title: "Paid Photos",
    desc: "Sell exclusive photo content to your fans.",
    icon: <PaidPhotosIcon />,
    enabled: true,
    isCoreChannel: true,
  },
  {
    id: "videos",
    title: "Paid Videos",
    desc: "Offer premium video content for your audience.",
    icon: <PaidVideoIcon />,
    enabled: true,
    isCoreChannel: true,
  },
  {
    id: "private-shows",
    title: "Private shows",
    desc: "Host paid private sessions with your fans.",
    icon: <PrivateShowsIcon />,
    enabled: false,
    isCoreChannel: true,
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    desc: "Offer recurring monthly VIP access and exclusive benefits.",
    icon: <SubscriptionsCrownIcon />,
    enabled: true,
    isCoreChannel: true,
  },
  {
    id: "bundles",
    title: "Content Bundles",
    desc: "Sell curated collections of your best content.",
    icon: <ContentBundlesIcon />,
    enabled: false,
  },
  {
    id: "social-links",
    title: "Social Link Unlocks",
    desc: "Charge for access to your private social links and profiles",
    icon: <SocialLinkIcon />,
    enabled: false,
  },
  {
    id: "custom-requests",
    title: "Custom Requests",
    desc: "Receive paid custom content and personalized experiences.",
    icon: <CustomRequestsIcon />,
    enabled: false,
  },
  {
    id: "private-shows-extended",
    title: "Private Shows (Extended)",
    desc: "Offer extended private sessions with your audience.",
    icon: <PrivateShowsExtendedIcon />,
    enabled: false,
  },
];

interface ControlFeature {
  id: string;
  name: string;
  desc: string;
  enabled: boolean;
}

const INITIAL_CONTROL_FEATURES: ControlFeature[] = [
  {
    id: "messaging",
    name: "Paid Messaging",
    desc: "Charge per direct message received",
    enabled: true,
  },
  {
    id: "tips",
    name: "Tips",
    desc: "Accept direct creator tips on your profile",
    enabled: true,
  },
  {
    id: "photos",
    name: "Paid Photos",
    desc: "Sell exclusive high-res photo unlockables",
    enabled: true,
  },
  {
    id: "shows",
    name: "Private Shows",
    desc: "Host 1-on-1 private video sessions",
    enabled: true,
  },
  {
    id: "videos",
    name: "Paid Videos",
    desc: "Sell premium video content access",
    enabled: false,
  },
  {
    id: "subscriptions",
    name: "Subscriptions",
    desc: "Offer recurring monthly VIP access",
    enabled: false,
  },
];

interface StepMonetizationControlsProps {
  onNext?: () => void;
  onBack?: () => void;
  onSkip?: () => void;
}

export default function StepMonetizationControls({
  onNext,
  onBack,
  onSkip,
}: StepMonetizationControlsProps) {
  const [options, setOptions] = useState<MonetizationOption[]>(INITIAL_OPTIONS);
  const [controlFeatures, setControlFeatures] = useState<ControlFeature[]>(INITIAL_CONTROL_FEATURES);
  const [isMatrixExpanded, setIsMatrixExpanded] = useState(false);

  const toggleOption = (id: string) => {
    setOptions((prev) =>
      prev.map((opt) => (opt.id === id ? { ...opt, enabled: !opt.enabled } : opt))
    );
  };

  const toggleFeature = (id: string) => {
    setControlFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f))
    );
  };

  const allEnabled = controlFeatures.length > 0 && controlFeatures.every((f) => f.enabled);

  const toggleAllFeatures = () => {
    const nextState = !allEnabled;
    setControlFeatures((prev) =>
      prev.map((f) => ({ ...f, enabled: nextState }))
    );
  };

  const activeCount = controlFeatures.filter((f) => f.enabled).length;

  // Core channels count
  const coreChannels = options.filter((o) => o.isCoreChannel);
  const activeCoreCount = coreChannels.filter((o) => o.enabled).length;

  const handleViewEarnings = () => {
    showToast("Earnings dashboard will activate once your creator profile goes live!", "info");
  };

  return (
    <div className={styles.pageWrapper}>
      {/* ── Hero Banner Section (Full Width with Left & Right Ambient Fade) ── */}
      <section className={styles.heroOuter}>
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>MONETIZATION CONTROL</span>
            <h1 className={styles.heroTitle}>Creator Monetization Controls</h1>
            <p className={styles.heroSubtitle}>
              Choose how you earn on SugarCribs. Enable or disable monetization
              features and adjust your preferences at any time.
            </p>

            {/* Monetization Status Card */}
            <div className={styles.statusCard}>
              <div className={styles.statusLeft}>
                <div className={styles.statusIconWrapper}>
                  <CrownIcon />
                </div>
                <div className={styles.statusTextGroup}>
                  <h3 className={styles.statusTitle}>Monetization Status</h3>
                  <p className={styles.statusSubtitle}>
                    {activeCoreCount} of {coreChannels.length} channels enabled.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className={styles.viewEarningsBtn}
                onClick={handleViewEarnings}
                id="view-earnings-btn"
              >
                View Earnings
              </button>
            </div>
          </div>

          {/* Right Visual Image */}
          <div className={styles.heroVisual}>
            <img
              src={heroImg}
              alt="Creator Monetization Controls"
              className={styles.heroImage}
            />
          </div>
        </div>
      </section>

      {/* ── Monetization Options Section (Max Width 1314px) ── */}
      <div className={styles.wrapper}>
        <section className={styles.optionsSection}>
          <div className={styles.optionsHeader}>
            <h2 className={styles.optionsTitle}>Monetization Options</h2>
            <p className={styles.optionsSubtitle}>
              Choose the experiences you want to offer and decide how you want
              members to access them.
            </p>
          </div>

          {/* 2-Column Options Grid */}
          <div className={styles.optionsGrid}>
            {options.map((opt) => (
              <div key={opt.id} className={styles.optionCard}>
                <div className={styles.optionLeft}>
                  <div className={styles.optionIconWrapper}>{opt.icon}</div>
                  <div className={styles.optionTextGroup}>
                    <h4 className={styles.optionTitle}>{opt.title}</h4>
                    <p className={styles.optionDesc}>{opt.desc}</p>
                  </div>
                </div>

                <div className={styles.optionControls}>
                  <span
                    className={
                      opt.enabled ? styles.badgeEnabled : styles.badgeDisabled
                    }
                  >
                    <span>{opt.enabled ? "Enabled" : "Disabled"}</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={opt.enabled}
                    onClick={() => toggleOption(opt.id)}
                    className={`${styles.switchButton} ${
                      opt.enabled
                        ? styles.switchButtonActive
                        : styles.switchButtonInactive
                    }`}
                    id={`toggle-${opt.id}`}
                  >
                    <span
                      className={`${styles.switchKnob} ${
                        opt.enabled
                          ? styles.switchKnobActive
                          : styles.switchKnobInactive
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── You Stay in Control Section (2-Column Grid with Hero Image) ── */}
        <section className={styles.controlSection}>
          <div className={styles.controlGrid}>
            {/* Left Column: Image */}
            <div className={styles.controlImageWrap}>
              <img
                src={stayInControlImg}
                alt="You Stay in Control"
                className={styles.controlImage}
                width={642}
                height={503}
              />
            </div>

            {/* Right Column: Content & 4 Control Cards */}
            <div className={styles.controlContent}>
              <span className={styles.controlTag}>CREATOR MONETIZATION CONTROLS</span>
              <h2 className={styles.controlTitle}>You Stay in Control</h2>
              <p className={styles.controlDesc}>
                SugarCribs empowers creators with complete autonomy over monetization features, custom pricing tiers, and real-time schedule availability.
              </p>

              {/* 4 Feature Control Cards */}
              <div className={styles.controlCardsList}>
                {/* Card 1: Feature Enable/ Disable Matrix */}
                <div
                  className={`${styles.controlCard} ${styles.controlCardActive}`}
                  onClick={() => setIsMatrixExpanded((prev) => !prev)}
                  role="button"
                  tabIndex={0}
                  id="card-feature-matrix"
                >
                  <div className={styles.controlCardLeft}>
                    <div className={styles.controlCardCheck}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                          stroke="#FD3C84"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className={styles.controlCardInfo}>
                      <h4 className={styles.controlCardTitle}>Feature Enable/ Disable Matrix</h4>
                      <p className={styles.controlCardDesc}>
                        Instant toggle whether to enable or disable each revenue channel.
                      </p>
                    </div>
                  </div>
                  <div className={styles.controlCardRight}>
                    <span className={styles.enabledPillBadge}>
                      <span>{activeCount} / {controlFeatures.length} Enabled</span>
                    </span>
                  </div>
                </div>

                {/* Optional Expandable Matrix on Card 1 Click */}
                {isMatrixExpanded && (
                  <div className={styles.matrixAccordionWrap}>
                    <div className={styles.matrixHeader}>
                      <div className={styles.matrixHeaderLeft}>
                        <div className={styles.matrixHeaderTitleRow}>
                          <button
                            type="button"
                            className={`${styles.matrixHeaderSwitchIcon} ${
                              allEnabled ? styles.matrixHeaderSwitchActive : ""
                            }`}
                            onClick={toggleAllFeatures}
                            aria-label={allEnabled ? "Disable all features" : "Enable all features"}
                            aria-pressed={allEnabled}
                            id="matrix-toggle-all-btn"
                          >
                            <span
                              className={`${styles.matrixSwitchThumb} ${
                                allEnabled ? styles.matrixSwitchThumbActive : ""
                              }`}
                            />
                          </button>
                          <h4 className={styles.matrixHeaderTitle}>Feature Enable / Disable Matrix</h4>
                        </div>
                      </div>
                    </div>
                    <div className={styles.matrixList}>
                      {controlFeatures.map((item) => (
                        <div
                          key={item.id}
                          className={`${styles.matrixRow} ${item.enabled ? styles.matrixRowActive : ""}`}
                          onClick={() => toggleFeature(item.id)}
                          role="button"
                          tabIndex={0}
                          id={`matrix-row-${item.id}`}
                        >
                          <div className={styles.matrixRowLeft}>
                            <div
                              className={`${styles.matrixCheckbox} ${
                                item.enabled ? styles.matrixCheckboxActive : ""
                              }`}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 22 22"
                                fill="none"
                                className={styles.matrixCheckIcon}
                              >
                                <path
                                  d="M18.332 6.41675L9.16536 15.5834L4.58203 11.0001"
                                  stroke="#FD3C84"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className={styles.matrixRowInfo}>
                              <div className={styles.matrixRowTitleLine}>
                                <span className={styles.matrixRowName}>{item.name}</span>
                                <span
                                  className={`${styles.matrixStatusPill} ${
                                    item.enabled ? styles.matrixStatusOn : styles.matrixStatusOff
                                  }`}
                                >
                                  ON
                                </span>
                              </div>
                              <span className={styles.matrixRowDesc}>{item.desc}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`${styles.matrixToggleSwitch} ${
                              item.enabled ? styles.matrixToggleOn : styles.matrixToggleOff
                            }`}
                            aria-pressed={item.enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFeature(item.id);
                            }}
                            id={`matrix-switch-${item.id}`}
                          >
                            <span className={styles.matrixToggleThumb} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Card 2: Custom pricing tiers */}
                <div className={styles.controlCard} id="card-custom-pricing">
                  <div className={styles.controlCardLeft}>
                    <div className={styles.controlCardCheck}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                          stroke="#FD3C84"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className={styles.controlCardInfo}>
                      <h4 className={styles.controlCardTitle}>Custom pricing tiers</h4>
                      <p className={styles.controlCardDesc}>
                        Set your own pricing for content and experiences.
                      </p>
                    </div>
                  </div>
                  <div className={styles.controlCardRight}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.chevronIcon}>
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Card 3: Real- Time Availability */}
                <div className={styles.controlCard} id="card-realtime-availability">
                  <div className={styles.controlCardLeft}>
                    <div className={styles.controlCardCheck}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                          stroke="#FD3C84"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className={styles.controlCardInfo}>
                      <h4 className={styles.controlCardTitle}>Real- Time Availability</h4>
                      <p className={styles.controlCardDesc}>
                        Manage your schedule and availability for private shows.
                      </p>
                    </div>
                  </div>
                  <div className={styles.controlCardRight}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.chevronIcon}>
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Card 4: Analytics & Performance */}
                <div className={styles.controlCard} id="card-analytics-performance">
                  <div className={styles.controlCardLeft}>
                    <div className={styles.controlCardCheck}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                          stroke="#FD3C84"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className={styles.controlCardInfo}>
                      <h4 className={styles.controlCardTitle}>Analytics & Performance</h4>
                      <p className={styles.controlCardDesc}>
                        Track your earnings and see what's working
                      </p>
                    </div>
                  </div>
                  <div className={styles.controlCardRight}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.chevronIcon}>
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
